// ---------------------------------------------------------------------------------------------
// EPOD - single source of truth for the human-readable "workflow status" shown across billing
// grids. Reads ONLY already-persisted fields the backend itself treats as authoritative
// (epod_gate_status, dealer_confirmation_status, dealer_confirmation_rejection_pending,
// epod_document_status/type, epod_signed_document_status) - it never recomputes or overrides
// backend eligibility, it only labels what the backend has already decided.
//
// Centralizing here so epodScreen.js/alReviewScreen.js/dealerConfirmationScreen.js never grow
// their own divergent copies of this mapping (each of those previously had a narrower, partially
// overlapping ad-hoc version - see friendlyStatus() in alReviewScreen.js/dealerConfirmationScreen.js
// and the inline "Dealer Condition"/"AL Review" valueGetters in epodScreen.js). Editing display text
// belongs HERE, in STATUS_LABELS - never re-add a status string inline in a component file.
//
// OCR (epod_ocr_status/epod_ocr/epod_ocr_failure_reason) is folded in below, but ONLY the
// current-upload-sequence result (epod_ocr.upload_seq === epod_gate.upload_seq) - the same
// staleness guard the "OCR Status" grid column already applies - and only while nothing more
// authoritative (AL review/dealer confirmation/late-upload gate decision) already applies. A
// resolved OCR result (success) never lingers as its own label: since OCR eligibility itself
// requires an OCR-eligible gate status (cleared/late_approved - see epodOcrWorker.py's
// ELIGIBLE_GATE_STATUSES), a current-seq success simply falls through to "Dealer Review Pending",
// the same status that already covered this row before OCR was folded in.
// ---------------------------------------------------------------------------------------------

export const STATUS_BADGE_CLASS = {
    success: 'badge badge-success',
    danger: 'badge badge-danger',
    warning: 'badge badge-warning',
    secondary: 'badge badge-secondary',
};

const DOCUMENT_TYPE_LABEL = { credit_note: 'Credit Note', cof: 'Certificate of Fulfilment (COF)' };

// Single mapping of internal state -> {text, style}. style keys into STATUS_BADGE_CLASS above.
export const STATUS_LABELS = {
    not_started: { text: 'POD Pending', style: 'secondary' },
    pod_submitted: { text: 'POD Submitted by Transporter', style: 'secondary' },
    ocr_pending: { text: 'OCR Pending', style: 'secondary' },
    ocr_in_progress: { text: 'OCR In Progress', style: 'secondary' },
    ocr_failed: { text: 'OCR Failed', style: 'danger' },
    late_upload_pending: { text: 'Awaiting AL Team Late Approval', style: 'warning' },
    late_upload_rejected: { text: 'Re-upload Required', style: 'danger' },
    ocr_al_review_pending: { text: 'OCR Failed - Awaiting AL Team Review', style: 'warning' },
    ocr_approved_manually: { text: 'Approved manually', style: 'success' },
    ocr_al_rejected: { text: 'Rejected by AL Team - Re-upload Required', style: 'danger' },
    awaiting_dealer_review: { text: 'Dealer Review Pending', style: 'warning' },
    dealer_clean: { text: 'Clean - Closed', style: 'success' },
    dealer_damage_closed: { text: 'Damage - Closed', style: 'success' },
    awaiting_al_review: { text: 'Awaiting AL Team Review', style: 'warning' },
    al_approved: { text: 'Approved by AL Team', style: 'success' },
    al_rejected_terminal: { text: 'Rejected - Workflow Closed', style: 'danger' },
    al_rejected_legacy: { text: 'Re-upload Required', style: 'danger' },
    cof_required: { text: 'COF Note Upload Required', style: 'warning' },
    completed: { text: 'Completed', style: 'success' },
};

// Tooltip copy for statuses where telling the user what happens next is useful. Deliberately not
// business logic - purely descriptive text, keyed off the same STATUS_LABELS keys. A status with
// no entry here just renders without a tooltip.
export const STATUS_TOOLTIP = {
    not_started: 'Awaiting transporter to upload POD',
    pod_submitted: 'POD submitted; processing will continue',
    ocr_pending: 'POD received; OCR processing has not started yet',
    ocr_in_progress: 'OCR processing is currently in progress',
    ocr_failed: 'OCR failed; POD can be re-uploaded',
    ocr_al_review_pending: 'OCR failed on the last allowed upload; the AL Team approves or rejects this e-POD',
    ocr_approved_manually: 'Verified by the AL Team instead of OCR',
    ocr_al_rejected: 'The AL Team rejected this e-POD; the transporter must upload a new one',
    awaiting_dealer_review: 'Awaiting dealer confirmation',
    awaiting_al_review: 'Awaiting AL Team approval/rejection',
    al_rejected_terminal: 'This workflow has been permanently rejected',
};

// Looks up the tooltip text for a {text, style} value already returned by
// getWorkflowDisplayStatus(), by identity against STATUS_LABELS - keeps the label->tooltip mapping
// centralized here instead of every caller re-deriving or duplicating it. The two one-off inline
// labels getWorkflowDisplayStatus builds itself (the "{doc type} - Signed Upload Pending" strings)
// aren't in STATUS_LABELS and correctly resolve to no tooltip.
export function getWorkflowStatusTooltip(resolved) {
    if (!resolved) return '';
    for (var key in STATUS_LABELS) {
        if (STATUS_LABELS[key] === resolved) return STATUS_TOOLTIP[key] || '';
    }
    return '';
}

// Resolves the OCR sub-state for the CURRENT upload cycle only, for a row whose epod_gate_status
// is already known to be OCR-eligible (cleared/late_approved). Returns null when OCR has either
// resolved successfully or offers no safe signal to display - callers fall through to whatever
// status they'd otherwise show.
//
// "eligibleGateStates" is the backend-sourced list of epod_gate_status values OCR can actually be
// queued against (epodLateUploadService's OCR_ELIGIBLE_STATES, via /billing/epodLateUploadOptions'
// ocr_eligible_states - fetched by the caller, never hardcoded here). Passing [] (its fail-closed
// default before that config loads) means "no OCR-pending signal available yet", not "OCR is not
// eligible" - so this never guesses a Pending/In Progress state without that config.
//
// Only two values are actually persisted by this codebase's own OCR result endpoint
// (epodOcrService.submitOcrResult: 'success' | 'failed' only - see its header comment). The
// standalone OCR worker script (epodOcrWorker.py) additionally defines 'processing'/'not_started'/
// 'manual_review' for its own optional direct-Mongo-write mode, which this Node backend's own
// contract says it does not use ("the OCR worker never writes this collection directly"). Handled
// here anyway, defensively, in case that mode is ever active - never assumed to be reachable.
/**
 * The AL decision on an e-POD that failed OCR on its last allowed upload - the same rule as the
 * backend's services/epodOcrReviewService.reviewState, on the row's CURRENT upload only.
 * `reviewOptions` = /epodLateUploadOptions' ocr_manual_review plus `eligibleGateStates`.
 * Returns null, { pending: true, reason, upload_no } or { pending: false, decided, review }.
 * A decision already taken is reported even without options (it is history, not an offer).
 */
export function getOcrReviewState(row, reviewOptions) {
    if (!row) return null;
    var gate = row.epod_gate;
    var ocr = row.epod_ocr;
    if (!gate || !ocr || Number(ocr.upload_seq) !== Number(gate.upload_seq)) return null;
    var review = row.epod_ocr_review;
    if (review && review.status && Number(review.upload_seq) === Number(gate.upload_seq)) {
        return { pending: false, decided: review.status, review: review };
    }
    var o = reviewOptions || {};
    if (!o.enabled) return null;
    if ((o.eligibleGateStates || []).indexOf(row.epod_gate_status) === -1) return null;
    if (row.epod_ocr_status === 'manual_review' && o.also_when_ocr_could_not_read) {
        return { pending: true, reason: 'ocr_could_not_read', upload_no: null };
    }
    if (row.epod_ocr_status === 'failed' && o.ocr_retry_enabled && gate.ocr_retry) {
        var cycleNo = Number(gate.ocr_retry.cycle_no) || 1;
        var limit = Number(cycleNo <= 1 ? o.free_uploads : o.free_uploads_after_approval);
        var uploads = Number(gate.ocr_retry.cycle_uploads);
        if (isFinite(limit) && limit >= 1 && isFinite(uploads) && uploads >= limit) {
            return { pending: true, reason: 'last_free_upload_failed', upload_no: uploads };
        }
    }
    return null;
}

// OCR stage label when the AL Team has decided (or must decide) the current upload; null otherwise.
function ocrReviewLabel(row, options) {
    var s = getOcrReviewState(row, options && options.ocrReview);
    if (!s) return null;
    if (s.pending) return STATUS_LABELS.ocr_al_review_pending;
    if (s.decided === 'approved') return STATUS_LABELS.ocr_approved_manually;
    if (s.decided === 'rejected') return STATUS_LABELS.ocr_al_rejected;
    return null;
}

function resolveCurrentOcrStage(row, eligibleGateStates, options) {
    var gate = row.epod_gate;
    var ocr = row.epod_ocr;
    var currentSeq = gate && gate.upload_seq;
    var raw = row.epod_ocr_status;
    var isCurrentResult = raw && ocr && Number(ocr.upload_seq) === Number(currentSeq);

    var reviewLabel = ocrReviewLabel(row, options);
    // A manual approval is a resolved result like an OCR pass - falls through to the next status.
    if (reviewLabel && reviewLabel !== STATUS_LABELS.ocr_approved_manually) return reviewLabel;

    if (isCurrentResult) {
        if (raw === 'success') return null;
        if (raw === 'processing') return STATUS_LABELS.ocr_in_progress;
        if (raw === 'failed' || raw === 'manual_review') return STATUS_LABELS.ocr_failed;
        if (raw === 'not_started') return STATUS_LABELS.ocr_pending;
        return null;
    }

    var eligible = eligibleGateStates || [];
    if (gate && eligible.indexOf(row.epod_gate_status) !== -1) {
        return STATUS_LABELS.ocr_pending;
    }
    return null;
}

/**
 * Resolves the single, composite, human-readable workflow status for one LR/consignment row.
 * Pure, synchronous, no side effects - reads fields directly off `row` (works whether that row
 * came from a wide/unprojected consignment query or a narrower queue-specific one; a field this
 * function doesn't find on a given screen's row set is simply treated as "not set", never guessed).
 *
 * `options.ocrEligibleGateStates` - the backend-sourced list of epod_gate_status values OCR is
 * actually queued against (same source as epodScreen.js's own epodOcrEligibleStates state,
 * fail-closed default []). Optional - callers that don't pass it simply never see an
 * OCR Pending/In Progress label (identical to this resolver's pre-OCR behavior).
 *
 * PRECEDENCE (highest first):
 *   4. AL Review decision                   (dealer_confirmation_status: al_approved | al_rejected | pending_al_review)
 *        4a. within al_approved only, refine by document/signed-upload progress:
 *            - epod_signed_document_status === 'signed_uploaded'      -> Completed
 *            - epod_document_status === 'cof_required'                -> COF Note Upload Required
 *            - epod_document_status === 'generated' + epod_document_id -> "{doc type} - Signed Upload Pending"
 *            - none of the above                                      -> Approved by AL Team
 *   3. Dealer Confirmation - Clean           (dealer_confirmation_status === closed_clean)
 *   3b. Legacy NDV-reject requeue           (dealer_confirmation_rejection_pending, only reachable
 *                                            when terminal_rejection was false at rejection time -
 *                                            requeueAfterRejection() is what clears
 *                                            dealer_confirmation_status back off the LR)
 *   2. Late-upload gate (Phase 2)            (epod_gate_status: late_pending_approval | late_rejected)
 *   1. OCR (current upload_seq only)         (epod_gate_status: cleared | late_approved, then see
 *                                            resolveCurrentOcrStage - Pending/In Progress/Failed)
 *   0. Dealer Review Pending                 (epod_gate_status: cleared | late_approved, OCR resolved
 *                                            or offered no signal)
 *   0b. POD Submitted by Transporter         (is_submitted_pod/pod_document present, no gate status
 *                                            at all yet - e.g. late-upload gate feature disabled)
 *   fallback: not_started ('POD Pending') - missing/null/unrecognized fields never render as a
 *             guessed terminal/completed/in-progress state.
 *
 * Terminal vs legacy rejection: both read the SAME al_rejected value on
 * dealer_confirmation_status - the distinguishing fact is persistence, not a separate field.
 * Under terminal_rejection:true the status is never cleared, so al_rejected is reached here and
 * labeled "Rejected - Workflow Closed". Under terminal_rejection:false, requeueAfterRejection()
 * unsets dealer_confirmation_status in the same request that sets it, so this branch is
 * structurally unreachable for a legacy rejection - dealer_confirmation_rejection_pending (branch
 * 3b) is what actually fires instead, labeled "Re-upload Required". Because dealer_confirmation_status
 * is checked FIRST and unconditionally, a current-cycle al_rejected is always reached before any
 * OCR/document field is even looked at - a stale prior-cycle epod_signed_document_status:
 * 'signed_uploaded' can never turn a current al_rejected (or pending_al_review/closed_clean) cycle
 * into "Completed", since those document fields are only ever consulted inside the al_approved
 * branch above, and al_approved/al_rejected/pending_al_review/closed_clean are mutually exclusive
 * values of the same field at any one time.
 *
 * WHY document/signed-upload fields are gated behind al_approved (not checked unconditionally
 * first): they are LR-level summary fields (epod_document_id/type/status,
 * epod_signed_document_status) that are only ever SET by generateDocument()/uploadSignedDocument()
 * - nothing in this codebase clears them on a later event. A genuine e-POD re-upload
 * (evaluateUpload() in epodLateUploadService.js touches only epod_gate_status/epod_gate - verified,
 * it never reads or clears any Phase 3/4 field) can start a BRAND NEW dealer-confirmation cycle
 * (resolveEligibleLr keys eligibility on epod_upload_seq/version, not on these summary fields) on an
 * LR that already carries a fully-completed OLD cycle's epod_signed_document_status:
 * 'signed_uploaded'. If that new cycle then reaches pending_al_review, al_rejected (including a
 * TERMINAL rejection), or closed_clean, the OLD 'signed_uploaded'/'generated'/'cof_required' values
 * are still sitting on the LR, unrelated to the CURRENT decision. dealer_confirmation_status is,
 * by contrast, always freshly overwritten by every submit/approve/reject call for the CURRENT
 * cycle - so checking it FIRST (and only consulting the document fields when it currently reads
 * al_approved, the one status those fields can legitimately refine) prevents a stale prior-cycle
 * document/signed field from ever masking a fresher terminal-rejected/pending/clean status.
 * Residual, intentionally-unclosed edge case: if a SECOND cycle also reaches al_approved but has
 * not yet triggered its OWN document generation, the still-stale document fields from the FIRST
 * cycle would be shown instead of a bare "Approved by AL Team". Closing this fully would require a
 * new field correlating epod_document_id to the dealer_confirmation_id it was generated for (or
 * clearing these fields on every fresh submission) - a backend change, out of scope for this
 * display-only resolver.
 *
 * OCR (epod_ocr_status/epod_ocr) is read only inside the cleared/late_approved branch, and only
 * ever for the row's CURRENT upload_seq (epod_ocr.upload_seq === epod_gate.upload_seq) - see
 * resolveCurrentOcrStage. A stale OCR result from a PRIOR upload cycle (e.g. cycle 1 succeeded,
 * cycle 2 just re-uploaded and hasn't been picked up yet) is therefore never shown as current -
 * exactly the same upload_seq guard the existing "OCR Status"/"OCR Failure Reason" grid columns
 * already rely on, centralized here instead of duplicated.
 */
export function getWorkflowDisplayStatus(row, options) {
    if (!row) return STATUS_LABELS.not_started;
    var ocrEligibleGateStates = (options && options.ocrEligibleGateStates) || [];

    if (row.dealer_confirmation_status === 'al_approved') {
        if (row.epod_signed_document_status === 'signed_uploaded') return STATUS_LABELS.completed;
        if (row.epod_document_status === 'cof_required') return STATUS_LABELS.cof_required;
        if (row.epod_document_status === 'generated' && row.epod_document_id) {
            var docLabel = DOCUMENT_TYPE_LABEL[row.epod_document_type] || 'Document';
            return { text: docLabel + ' - Signed Upload Pending', style: 'warning' };
        }
        return STATUS_LABELS.al_approved;
    }
    if (row.dealer_confirmation_status === 'al_rejected') return STATUS_LABELS.al_rejected_terminal;
    if (row.dealer_confirmation_status === 'pending_al_review') return STATUS_LABELS.awaiting_al_review;
    if (row.dealer_confirmation_status === 'closed_clean') return STATUS_LABELS.dealer_clean;
    if (row.dealer_confirmation_status === 'closed_damage') return STATUS_LABELS.dealer_damage_closed;

    if (row.dealer_confirmation_rejection_pending) return STATUS_LABELS.al_rejected_legacy;

    // Independent dealer flow: Phase 3 onwards never waits on the e-POD, so with no dealer decision
    // yet the LR is simply with the dealer - the late-upload gate and OCR (Phase 2) must not
    // override that. Their progress is shown by getPodVerificationStatus() below instead.
    if (row.epod_dealer_mode === 'independent') return STATUS_LABELS.awaiting_dealer_review;

    if (row.epod_gate_status === 'late_pending_approval') return STATUS_LABELS.late_upload_pending;
    if (row.epod_gate_status === 'late_rejected') return STATUS_LABELS.late_upload_rejected;
    if (row.epod_gate_status === 'cleared' || row.epod_gate_status === 'late_approved') {
        var ocrStage = resolveCurrentOcrStage(row, ocrEligibleGateStates, options);
        if (ocrStage) return ocrStage;
        return STATUS_LABELS.awaiting_dealer_review;
    }

    if (row.is_submitted_pod === 1 || !!row.pod_document) return STATUS_LABELS.pod_submitted;

    return STATUS_LABELS.not_started;
}

/**
 * Phase 2 (e-POD upload / late-upload gate / OCR) status, for an LR on the independent dealer flow
 * ONLY - null for every other row, so legacy screens are unchanged. Independent of the dealer track:
 * getWorkflowDisplayStatus() above reports where the LR stands for the dealer / AL review; this
 * reports where the LSP's own e-POD stands, including WHY OCR failed (the LSP needs that to fix the
 * upload). Same upload_seq staleness guard as resolveCurrentOcrStage - a result from an earlier
 * upload is never shown as current.
 */
export function getPodVerificationStatus(row, options) {
    if (!row || row.epod_dealer_mode !== 'independent') return null;

    var gateStatus = row.epod_gate_status;
    if (gateStatus === 'late_pending_approval') return STATUS_LABELS.late_upload_pending;
    if (gateStatus === 'late_rejected') return STATUS_LABELS.late_upload_rejected;

    var ocr = row.epod_ocr;
    var gate = row.epod_gate;
    var raw = row.epod_ocr_status;
    var isCurrentResult = raw && ocr && gate && Number(ocr.upload_seq) === Number(gate.upload_seq);
    var reviewLabel = ocrReviewLabel(row, options);
    if (reviewLabel) return reviewLabel;
    if (isCurrentResult) {
        if (raw === 'success') return { text: 'OCR Verified', style: 'success' };
        if (raw === 'processing') return STATUS_LABELS.ocr_in_progress;
        if (raw === 'failed' || raw === 'manual_review') {
            var reason = ocr.failure_reason || ocr.failure_code;
            return { text: reason ? 'OCR Failed - ' + reason : STATUS_LABELS.ocr_failed.text, style: 'danger' };
        }
        if (raw === 'not_started') return STATUS_LABELS.ocr_pending;
    }

    var eligible = (options && options.ocrEligibleGateStates) || [];
    if (gate && eligible.indexOf(gateStatus) !== -1) return STATUS_LABELS.ocr_pending;

    if (row.is_submitted_pod === 1 || !!row.pod_document) return STATUS_LABELS.pod_submitted;
    return STATUS_LABELS.not_started;
}
